'use client';

import { lazy, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { appendSearchParamsToUrl, removeSearchParamsFromUrl, sanitizeNavigationUrl, wait } from '@bridge/utils';
import { create } from 'zustand';

import SheetWrapper from '../components/SheetWrapper';
import { SheetContext } from '../components/SheetContext';

import { parseUrlOverlayProps } from './urlPropSchema';

import type { UrlPropSchema } from './urlPropSchema';
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react';
import type { UseBoundStore, StoreApi } from 'zustand';

/** Props injected by `SheetRenderer` into every sheet component. */
export interface BaseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// `ComponentType<any>` (rather than `ComponentType<BaseSheetProps>`) lets the registry hold
// components whose prop shapes EXTEND BaseSheetProps with required fields (e.g. an `entityId`).
// Per-key prop types are recovered via `InferComponentProps<T>` and surfaced through
// `SheetProps<R, ID, Overrides>`, so call-site type safety is preserved.
// The renderer assumes every component accepts BaseSheetProps (it injects `open`/`onOpenChange`
// unconditionally) — this is a documented contract, not a type-checked one.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SheetImportFnAny = () => Promise<{ default: ComponentType<any> }>;

/** Presentation returned by `renderErrorFallback`: the shell renders the title/description, the fallback the body. */
type ErrorFallbackContent = { title: string; description?: string; content: ReactNode };

/**
 * Object form of a registry entry that co-locates URL-openability with the component. The
 * `urlOpenable` schema is the opt-in trust boundary for `?sheet=<id>&sheetProps=<JSON>`: a sheet
 * whose entry omits it — or that uses the bare-import form — can never be opened from the URL,
 * and props failing the schema are rejected. App-driven `openSheet` is unaffected. See `UrlPropSchema`.
 */
export interface SheetRegistryEntryConfig {
  component: SheetImportFnAny;
  urlOpenable?: UrlPropSchema;
}

/**
 * A registry entry is either a bare dynamic import or the `{ component, urlOpenable }` config.
 * Use the config form only for sheets opting in to URL opening; bare imports stay terse.
 */
export type SheetRegistryEntry = SheetImportFnAny | SheetRegistryEntryConfig;

export type SheetRegistryMap = Record<string, SheetRegistryEntry>;

type SheetImportFn = () => Promise<{ default: ComponentType<BaseSheetProps> }>;

type InferComponentProps<T> = T extends SheetRegistryEntryConfig
  ? InferComponentProps<T['component']>
  : T extends () => Promise<{ default: ComponentType<infer P> }>
    ? P
    : never;

type DefaultSheetProps<R extends SheetRegistryMap, ID extends keyof R> = Omit<
  InferComponentProps<R[ID]>,
  'open' | 'onOpenChange'
>;

const resolveSheetImport = (entry: SheetRegistryEntry): SheetImportFnAny =>
  typeof entry === 'function' ? entry : entry.component;

const resolveSheetUrlSchema = (entry: SheetRegistryEntry): UrlPropSchema | undefined =>
  typeof entry === 'function' ? undefined : entry.urlOpenable;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Resolved props for a sheet identifier, accounting for optional per-sheet overrides.
 * Apps pass an `Overrides` map to `createSheetStore` when they want to accept a
 * narrower (e.g. `{ paymentEventId: string } | { paymentEvent: PaymentEvent }`) shape
 * than the component's full prop type.
 */
export type SheetProps<
  R extends SheetRegistryMap,
  ID extends keyof R,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> = ID extends keyof Overrides ? Overrides[ID] : DefaultSheetProps<R, ID>;

export interface SheetInstance<R extends SheetRegistryMap, ID extends keyof R = keyof R> {
  id: string;
  identifier: ID;
  component: LazyExoticComponent<ComponentType<BaseSheetProps>>;
  props: Record<string, unknown> | null;
  isOpen: boolean;
  /** Flag to disable animations during content replacement */
  disableAnimations?: boolean;
  /** Track the selected tab for this sheet instance */
  selectedTab?: string;
}

interface SheetOpenOptions {
  /** Replaces the entire stack with the new sheet so no back navigation remains. */
  clearStack?: boolean;
  replace?: boolean;
  initialTab?: string;
}

interface SheetState<R extends SheetRegistryMap> {
  activeSheets: SheetInstance<R>[];
}

interface SheetActions<R extends SheetRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> {
  openSheet: <ID extends keyof R>(identifier: ID, props?: SheetProps<R, ID, Overrides>, options?: SheetOpenOptions) => string;
  closeSheet: () => Promise<void>;
  closeAllSheets: () => void;
  navigateBack: () => void;
  enableAnimations: () => void;
  setSheetTab: (tab: string) => void;
}

export type SheetStore<R extends SheetRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> = SheetState<R> &
  SheetActions<R, Overrides>;

export type GetSheetUrl<R extends SheetRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> = <
  ID extends keyof R,
>(
  url: string,
  sheetName: ID,
  sheetProps?: SheetProps<R, ID, Overrides>,
) => string;

export interface UseSheetReturn<
  R extends SheetRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> {
  openSheet: SheetActions<R, Overrides>['openSheet'];
  /**
   * Opens the sheet and mirrors it in the URL via `?sheet=<id>&sheetProps=<encoded urlProps>`.
   * - `sheetProps`: full payload for immediate display (can include rich objects).
   * - `urlProps`: minimal, serializable payload for deep linking (typically IDs).
   * Uses `history.replaceState` to avoid triggering an RSC refetch.
   */
  openSheetWithDeepLink: <ID extends keyof R>(
    identifier: ID,
    sheetProps: SheetProps<R, ID, Overrides>,
    urlProps: SheetProps<R, ID, Overrides>,
    options?: SheetOpenOptions,
  ) => void;
  closeSheet: () => Promise<void>;
  closeAllSheets: () => void;
  navigateBack: () => void;
  canNavigateBack: () => boolean;
  setSheetTab: (tab: string) => void;
}

export interface CreateSheetStoreOptions<R extends SheetRegistryMap = SheetRegistryMap> {
  /**
   * Base URL used by `getSheetUrl` and `openSheetWithDeepLink` when a relative URL is passed in.
   * Defaults to `window.location.origin` at call time, which only works client-side. Pass an
   * explicit value (e.g. an env-derived domain) when the deep-link helpers may run during SSR/RSC,
   * such as when building static `<Link href>` values from server components.
   */
  baseUrl?: string;
  /**
   * Sheet identifiers that `preloadSheets` should skip. Use for dev-only sheets whose
   * chunks should not ship in production bundles.
   */
  excludeFromPreload?: readonly (keyof R)[];
}

export interface CreateSheetStoreResult<
  R extends SheetRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> {
  useSheetStore: UseBoundStore<StoreApi<SheetStore<R, Overrides>>>;
  useSheet: () => UseSheetReturn<R, Overrides>;
  preloadSheets: () => Promise<void>;
  SheetRenderer: ComponentType<{
    isLoggedIn: boolean;
    /**
     * Contains a render error thrown by the active sheet (e.g. a suspended query
     * rejecting) so it degrades to this fallback instead of crashing the app.
     * Omit to preserve today's behavior of letting the error escape uncaught.
     */
    renderErrorFallback?: (args: {
      error: unknown;
      reset: () => void;
    }) => ErrorFallbackContent;
  }>;
  /**
   * Builds a deep-link URL that opens the given sheet with `sheetProps` when followed.
   * The URL contract (`?sheet=<id>&sheetProps=<encoded JSON>`) round-trips with the
   * `SheetRenderer` returned from this same factory.
   */
  getSheetUrl: GetSheetUrl<R, Overrides>;
}

/**
 * Factory that returns a Zustand-backed sheet store, a preloader, and a pre-wired
 * `<SheetRenderer />` closed over the store. Each app instantiates its own instance
 * with its registry, keeping @bridge/ui free of app-specific deps.
 *
 * Pass `Overrides` as an optional second type parameter to specialize the props
 * surface for specific sheet IDs (e.g. to accept `{ entityId: string }` in addition
 * to the default `{ entity: Entity }` the component's signature expects).
 */
export function createSheetStore<
  R extends SheetRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
>(sheetRegistry: R, factoryOptions: CreateSheetStoreOptions<R> = {}): CreateSheetStoreResult<R, Overrides> {
  const { baseUrl: configuredBaseUrl, excludeFromPreload } = factoryOptions;
  const excludedPreloadIds = new Set<keyof R>(excludeFromPreload ?? []);
  const resolveBaseUrl = (): string =>
    configuredBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');

  const getSheetUrl: GetSheetUrl<R, Overrides> = (url, sheetName, sheetProps) => {
    const params: Record<string, string> = { sheet: String(sheetName) };
    if (sheetProps) {
      params.sheetProps = encodeURIComponent(JSON.stringify(sheetProps));
    }
    return appendSearchParamsToUrl(url, params, resolveBaseUrl());
  };

  const useSheetStore = create<SheetStore<R, Overrides>>((set) => ({
    activeSheets: [],

    openSheet: (identifier, props, options) => {
      const { clearStack = false, replace = true, initialTab } = options ?? {};
      const entry = sheetRegistry[identifier];

      if (!entry) {
        console.error(`Sheet "${String(identifier)}" not found in registry`);
        return '';
      }

      const LazyComponent = lazy(resolveSheetImport(entry) as SheetImportFn);
      const sheetId = crypto.randomUUID();

      set((state) => {
        if (clearStack) {
          const newSheet: SheetInstance<R> = {
            id: sheetId,
            identifier,
            component: LazyComponent,
            props: isRecord(props) ? props : null,
            isOpen: true,
            disableAnimations: state.activeSheets.length > 0,
            selectedTab: initialTab,
          };

          return {
            activeSheets: [newSheet],
          };
        }

        // If replace is true and there are existing sheets, add the new sheet to the stack
        // but keep the current sheet in the stack for navigation
        if (replace && state.activeSheets.length > 0) {
          const newSheet: SheetInstance<R> = {
            id: sheetId,
            identifier,
            component: LazyComponent,
            props: (props as Record<string, unknown> | undefined) ?? null,
            isOpen: true,
            disableAnimations: true, // Disable animations when replacing content
            selectedTab: initialTab,
          };

          return {
            activeSheets: [...state.activeSheets, newSheet],
          };
        }

        const newSheet: SheetInstance<R> = {
          id: sheetId,
          identifier,
          component: LazyComponent,
          props: (props as Record<string, unknown> | undefined) ?? null,
          isOpen: true,
          disableAnimations: false,
          selectedTab: initialTab,
        };

        return {
          activeSheets: [...state.activeSheets, newSheet],
        };
      });

      return sheetId;
    },

    closeSheet: async () => {
      set((state) => ({
        activeSheets: state.activeSheets.map((sheet) => ({
          ...sheet,
          isOpen: false,
          disableAnimations: false, // Re-enable animations for close animation
        })),
      }));

      await wait(300);
      set({ activeSheets: [] });
    },

    closeAllSheets: () => {
      set({ activeSheets: [] });
    },

    enableAnimations: () => {
      set((state) => ({
        activeSheets: state.activeSheets.map((sheet) => ({
          ...sheet,
          disableAnimations: false,
        })),
      }));
    },

    setSheetTab: (tab) => {
      set((state) => {
        if (state.activeSheets.length === 0) return state;

        const updatedSheets = [...state.activeSheets];
        const currentSheetIndex = updatedSheets.length - 1;
        updatedSheets[currentSheetIndex] = {
          ...updatedSheets[currentSheetIndex],
          selectedTab: tab,
        };

        return {
          activeSheets: updatedSheets,
        };
      });
    },

    navigateBack: () => {
      set((state) => {
        // If there's only one sheet or no sheets, can't navigate back
        if (state.activeSheets.length <= 1) {
          return state;
        }

        // Remove the current (top) sheet from the stack, revealing the previous one
        const newActiveSheets = state.activeSheets.slice(0, -1);

        // Re-enable animations on the previous sheet (now becoming current)
        if (newActiveSheets.length > 0) {
          newActiveSheets[newActiveSheets.length - 1] = {
            ...newActiveSheets[newActiveSheets.length - 1],
            disableAnimations: true,
            isOpen: true,
          };
        }

        return {
          activeSheets: newActiveSheets,
        };
      });
    },
  }));

  const preloadSheets = async (): Promise<void> => {
    const preloadPromises = Object.entries(sheetRegistry)
      .filter(([identifier]) => !excludedPreloadIds.has(identifier as keyof R))
      .map(async ([identifier, entry]) => {
        try {
          await resolveSheetImport(entry)();
        } catch (error) {
          console.error(`Failed to preload sheet "${identifier}":`, error);
        }
      });

    await Promise.all(preloadPromises);
  };

  function useSheet(): UseSheetReturn<R, Overrides> {
    const openSheet = useSheetStore((state) => state.openSheet);
    const closeSheet = useSheetStore((state) => state.closeSheet);
    const closeAllSheets = useSheetStore((state) => state.closeAllSheets);
    const navigateBack = useSheetStore((state) => state.navigateBack);
    const setSheetTab = useSheetStore((state) => state.setSheetTab);
    const activeSheets = useSheetStore((state) => state.activeSheets);

    const canNavigateBack = (): boolean => activeSheets.length > 1;

    const openSheetWithDeepLink = <ID extends keyof R>(
      identifier: ID,
      sheetProps: SheetProps<R, ID, Overrides>,
      urlProps: SheetProps<R, ID, Overrides>,
      options?: SheetOpenOptions,
    ): void => {
      openSheet(identifier, sheetProps, options);

      if (typeof window === 'undefined') return;

      const currentUrl = window.location.pathname + window.location.search;
      const sheetUrl = getSheetUrl(currentUrl, identifier, urlProps);
      window.history.replaceState(null, '', sanitizeNavigationUrl(sheetUrl));
    };

    return {
      openSheet,
      openSheetWithDeepLink,
      closeSheet,
      closeAllSheets,
      navigateBack,
      canNavigateBack,
      setSheetTab,
    };
  }

  function SheetRenderer({
    isLoggedIn,
    renderErrorFallback,
  }: {
    isLoggedIn: boolean;
    renderErrorFallback?: (args: {
      error: unknown;
      reset: () => void;
    }) => ErrorFallbackContent;
  }): ReactNode {
    const activeSheets = useSheetStore((state) => state.activeSheets);
    const openSheet = useSheetStore((state) => state.openSheet);
    const closeSheet = useSheetStore((state) => state.closeSheet);
    const navigateBack = useSheetStore((state) => state.navigateBack);
    const setSheetTab = useSheetStore((state) => state.setSheetTab);

    const currentSheet = activeSheets.length > 0 ? activeSheets[activeSheets.length - 1] : null;
    const canNavigateBack = activeSheets.length > 1;

    const searchParams = useSearchParams();
    const router = useRouter();

    // Track previous values for direction calculation
    const prevValuesRef = useRef<{ stackLength: number; sheetId: string | undefined }>({
      stackLength: 0,
      sheetId: undefined,
    });

    // Compute direction synchronously during render based on previous values
    let direction: 'next' | 'previous' | null = null;
    const currentStackLength = activeSheets.length;
    const currentSheetId = currentSheet?.id;
    const { stackLength: prevStackLength, sheetId: prevSheetId } = prevValuesRef.current;

    if (currentSheetId && currentSheetId !== prevSheetId) {
      if (currentStackLength > prevStackLength) {
        direction = 'previous';
      } else if (currentStackLength < prevStackLength) {
        direction = 'next';
      } else {
        direction = 'previous';
      }
    }

    useEffect(() => {
      prevValuesRef.current = {
        stackLength: currentStackLength,
        sheetId: currentSheetId,
      };
    }, [currentStackLength, currentSheetId]);

    // Sync sheet state with URL search parameters (only when logged in)
    useEffect(() => {
      // Never open sheets from URL params when not logged in.
      // The ?next param on login preserves the URL so the sheet opens after auth.
      if (!isLoggedIn) {
        return;
      }

      const sheetParam = searchParams?.get('sheet') as keyof R | null;
      if (!sheetParam || !(sheetParam in sheetRegistry)) return;

      // Opt-in gate: a sheet whose registry entry has no `urlOpenable` schema is never opened from the URL.
      const urlPropSchema = resolveSheetUrlSchema(sheetRegistry[sheetParam]);
      if (!urlPropSchema) return;

      if (activeSheets.some((sheet) => sheet.identifier === sheetParam)) return;

      const result = parseUrlOverlayProps(searchParams?.get('sheetProps'), urlPropSchema);
      if (!result.ok) {
        console.warn(`Ignoring URL-opened sheet "${String(sheetParam)}": invalid or unvalidated props`);
        return;
      }

      // Validated URL input crossing into typed territory; the schema is the trust boundary for this cast.
      openSheet(sheetParam, result.data as SheetProps<R, typeof sheetParam, Overrides>);
    }, [searchParams, openSheet, isLoggedIn, activeSheets]);

    // Override closeSheet to also update URL when sheet is closed
    const handleCloseSheet = useCallback(() => {
      closeSheet();

      const currentUrl = window.location.href;
      // URL helpers from @bridge/utils need a base URL; at runtime in the browser,
      // window.location.origin is correct (same approach as table-filters/url.ts).
      const cleanUrl = removeSearchParamsFromUrl(currentUrl, ['sheet', 'sheetProps'], window.location.origin);
      router.replace(sanitizeNavigationUrl(cleanUrl));
    }, [closeSheet, router]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          handleCloseSheet();
        }
      },
      [handleCloseSheet],
    );

    const presentation = useMemo(
      () =>
        currentSheet
          ? {
              contentKey: currentSheet.id,
              direction,
              disableAnimations: currentSheet.disableAnimations ?? false,
              initialTab: currentSheet.selectedTab,
              onTabChange: setSheetTab,
            }
          : undefined,
      [currentSheet, direction, setSheetTab],
    );
    const sheetContextValue = useMemo(
      () => ({ canNavigateBack, navigateBack, presentation }),
      [canNavigateBack, navigateBack, presentation],
    );

    if (!currentSheet || !presentation) {
      return null;
    }

    const SpecificSheet = currentSheet.component;
    const sheetSpecificProps = currentSheet.props ?? {};

    const combinedProps: BaseSheetProps & Record<string, unknown> = {
      open: currentSheet.isOpen,
      onOpenChange: handleOpenChange,
      ...sheetSpecificProps,
    };

    const sheetNode = <SpecificSheet key={currentSheet.id} {...combinedProps} />;

    return (
      <SheetContext.Provider value={sheetContextValue}>
        {renderErrorFallback ? (
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => {
                  const fallback = renderErrorFallback({ error, reset: resetErrorBoundary });
                  return (
                    <SheetWrapper
                      description={fallback.description}
                      onOpenChange={handleOpenChange}
                      open={currentSheet.isOpen}
                      title={fallback.title}
                    >
                      {fallback.content}
                    </SheetWrapper>
                  );
                }}
                onReset={reset}
                resetKeys={[currentSheet.id]}
              >
                {sheetNode}
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        ) : (
          sheetNode
        )}
      </SheetContext.Provider>
    );
  }

  return { useSheetStore, useSheet, preloadSheets, SheetRenderer, getSheetUrl };
}
